import { Type } from 'class-transformer';
import { IsArray, IsBoolean, IsNumber, IsOptional, IsString, ValidateNested } from 'class-validator';

/**
 * Configuration for TextPlugin animations
 * Animates text content character by character
 */
export class TextPluginConfigDto {
	/**
	 * The text content to animate to
	 * Can include HTML tags for styling
	 */
	@IsOptional()
	@IsString()
	text?: string;

	/**
	 * Character delimiter for text splitting
	 * Default: "" (empty string, splits every character)
	 */
	@IsOptional()
	@IsString()
	delimiter?: string;

	/**
	 * Speed of text animation
	 * Higher values = faster animation
	 */
	@IsOptional()
	@IsNumber()
	speed?: number;

	/**
	 * Enable scramble text effect during animation
	 */
	@IsOptional()
	@IsBoolean()
	scrambleText?: boolean;
}

/**
 * Configuration for DrawSVG animations
 * Animates the drawing of SVG paths
 */
export class DrawSVGConfigDto {
	/**
	 * Draw animation value
	 * Can be: "0% 100%" (draw from start to end), "50% 50%" (start from center),
	 * true (draw entire path), or [0, 1] (numeric range)
	 */
	@IsOptional()
	value?: string | boolean | [number, number];

	/**
	 * Whether to use live path measurement
	 * Set to false for better performance with static paths
	 */
	@IsOptional()
	@IsBoolean()
	live?: boolean;
}

/**
 * Configuration for MorphSVG animations
 * Morphs one SVG shape into another
 */
export class MorphSVGConfigDto {
	/**
	 * Target shape selector or SVG path string
	 * The shape to morph into
	 */
	@IsString()
	shape: string;

	/**
	 * Shape matching algorithm index
	 * Different values produce different morphing effects (0-100)
	 */
	@IsOptional()
	@IsNumber()
	shapeIndex?: number;

	/**
	 * Enable smooth morphing between shapes
	 * Adds curves for smoother transitions
	 */
	@IsOptional()
	@IsBoolean()
	smooth?: boolean;

	/**
	 * Transform origin for the morph
	 * Example: "center center", "50% 50%"
	 */
	@IsOptional()
	@IsString()
	origin?: string;

	/**
	 * Morph type
	 * "linear" = straight morphing, "rotational" = rotating morph
	 */
	@IsOptional()
	@IsString()
	type?: 'linear' | 'rotational';

	/**
	 * Map specific points between shapes
	 * Array of point indices for precise control
	 */
	@IsOptional()
	@IsArray()
	map?: number[];
}

/**
 * Configuration for MotionPath animations
 * Animates elements along a defined SVG path
 */
export class MotionPathConfigDto {
	/**
	 * The SVG path to follow
	 * Can be a path string or selector to an SVG path element
	 */
	@IsString()
	path: string;

	/**
	 * Element to align to the path
	 * Selector or element reference
	 */
	@IsOptional()
	@IsString()
	align?: string;

	/**
	 * Auto-rotate the element along the path
	 * true = auto-rotate, false = no rotation, number = rotation offset in degrees
	 */
	@IsOptional()
	autoRotate?: boolean | number;

	/**
	 * Alignment origin point [x, y]
	 * Values from 0 to 1 (0.5 = center)
	 */
	@IsOptional()
	@IsArray()
	alignOrigin?: [number, number];

	/**
	 * Start position on the path (0-1)
	 * 0 = beginning of path, 1 = end of path
	 */
	@IsOptional()
	@IsNumber()
	start?: number;

	/**
	 * End position on the path (0-1)
	 * 0 = beginning of path, 1 = end of path
	 */
	@IsOptional()
	@IsNumber()
	end?: number;

	/**
	 * Offset position from the path
	 * { x: number, y: number }
	 */
	@IsOptional()
	offsetX?: number;

	@IsOptional()
	offsetY?: number;

	/**
	 * Whether to use local coordinates
	 */
	@IsOptional()
	@IsBoolean()
	useLocalCoordinates?: boolean;
}

/**
 * Configuration for SplitText animations
 * Splits text into animatable characters, words, or lines
 */
export class SplitTextConfigDto {
	/**
	 * How to split the text
	 * Can combine: "chars,words,lines"
	 */
	@IsOptional()
	@IsString()
	type?: string;

	/**
	 * CSS class to apply to character elements
	 */
	@IsOptional()
	@IsString()
	charsClass?: string;

	/**
	 * CSS class to apply to word elements
	 */
	@IsOptional()
	@IsString()
	wordsClass?: string;

	/**
	 * CSS class to apply to line elements
	 */
	@IsOptional()
	@IsString()
	linesClass?: string;

	/**
	 * Position type for split elements
	 */
	@IsOptional()
	@IsString()
	position?: 'absolute' | 'relative';

	/**
	 * Whether to reduce white space in the split text
	 */
	@IsOptional()
	@IsBoolean()
	reduceWhiteSpace?: boolean;

	/**
	 * Special handling for words that should not be broken
	 */
	@IsOptional()
	@IsString()
	wordDelimiter?: string;

	/**
	 * Tag type to use for split elements
	 */
	@IsOptional()
	@IsString()
	tag?: string;

	/**
	 * Whether to preserve spaces as separate elements
	 */
	@IsOptional()
	@IsBoolean()
	preserveSpaces?: boolean;
}

/**
 * Configuration for Flip animations
 * Animates layout changes smoothly using FLIP technique
 */
export class FlipConfigDto {
	/**
	 * Target elements to flip
	 * CSS selector string
	 */
	@IsOptional()
	@IsString()
	targets?: string;

	/**
	 * Use absolute positioning during animation
	 * Helps prevent layout shifts
	 */
	@IsOptional()
	@IsBoolean()
	absolute?: boolean;

	/**
	 * Animate scale changes
	 */
	@IsOptional()
	@IsBoolean()
	scale?: boolean;

	/**
	 * Use simple flip algorithm
	 * Faster but less accurate for complex layouts
	 */
	@IsOptional()
	@IsBoolean()
	simple?: boolean;

	/**
	 * Animate rotation changes
	 */
	@IsOptional()
	@IsBoolean()
	rotation?: boolean;

	/**
	 * Custom properties to track
	 * Array of CSS property names
	 */
	@IsOptional()
	@IsArray()
	props?: string[];

	/**
	 * Whether to clear inline styles after animation
	 */
	@IsOptional()
	@IsBoolean()
	clearProps?: boolean;

	/**
	 * Whether to handle nested flip animations
	 */
	@IsOptional()
	@IsBoolean()
	nested?: boolean;

	/**
	 * Z-index during animation
	 */
	@IsOptional()
	@IsNumber()
	zIndex?: number;
}

/**
 * Configuration for CustomEase animations
 * Uses custom easing curves
 */
export class CustomEaseConfigDto {
	/**
	 * Name of the registered custom ease
	 * Must be registered with CustomEase.create() first
	 */
	@IsString()
	name: string;

	/**
	 * SVG path data for the ease curve
	 * Optional if ease is already registered
	 */
	@IsOptional()
	@IsString()
	path?: string;
}

/**
 * Main plugin configuration container
 * Contains all plugin-specific animation settings
 */
export class AnimationPluginConfigDto {
	/**
	 * TextPlugin configuration
	 * For animating text content
	 */
	@IsOptional()
	@ValidateNested()
	@Type(() => TextPluginConfigDto)
	text?: TextPluginConfigDto | string; // Allow simple string for convenience

	/**
	 * DrawSVG configuration
	 * For animating SVG path drawing
	 */
	@IsOptional()
	drawSVG?: string | boolean | [number, number] | DrawSVGConfigDto;

	/**
	 * MorphSVG configuration
	 * For morphing between SVG shapes
	 */
	@IsOptional()
	@ValidateNested()
	@Type(() => MorphSVGConfigDto)
	morphSVG?: MorphSVGConfigDto;

	/**
	 * MotionPath configuration
	 * For animating along paths
	 */
	@IsOptional()
	@ValidateNested()
	@Type(() => MotionPathConfigDto)
	motionPath?: MotionPathConfigDto;

	/**
	 * SplitText configuration
	 * For splitting and animating text
	 */
	@IsOptional()
	@ValidateNested()
	@Type(() => SplitTextConfigDto)
	splitText?: SplitTextConfigDto;

	/**
	 * Flip configuration
	 * For smooth layout animations
	 */
	@IsOptional()
	@ValidateNested()
	@Type(() => FlipConfigDto)
	flip?: FlipConfigDto;

	/**
	 * CustomEase configuration
	 * For using custom easing functions
	 */
	@IsOptional()
	customEase?: string | CustomEaseConfigDto;

	/**
	 * ScrollTrigger configuration
	 * For scroll-based animations
	 * Can be a configuration object or trigger selector
	 */
	@IsOptional()
	scrollTrigger?: any;

	/**
	 * Allow additional plugin properties for future plugins
	 */
	[key: string]: any;
}